import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiFetch, apiUrl, resolveMediaUrl } from '@/lib/api';
import { useSiteMasterSession } from '@/hooks/useSiteMasterSession';
import { siteMasterPaths } from '@/lib/siteMasterPaths';
import { Spinner } from '@/components/ui/Spinner';
import { cn } from '@/lib/cn';
import type { MiniproAdRow } from '@/components/MiniprogramAdsPanel';

type MiniprogramOption = { id: number; name: string };

const PER_PAGE = 10;

const inputClass =
  'w-full rounded-2xl border-2 border-slate-200 px-4 py-3.5 text-lg focus:border-amber-500 focus:outline-none focus:ring-4 focus:ring-amber-500/20';

const emptyForm = (mpId: number): Omit<MiniproAdRow, 'is_active'> => ({
  id: 0,
  miniprogram_id: mpId,
  title: '',
  banner_image_path: '',
  link_type: 'entry',
  entry_id: 0,
  target_image_path: '',
  site_ids: [],
  valid_start_date: new Date().toISOString().slice(0, 10),
  valid_end_date: new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10),
  sort_order: 0,
  enabled: true,
});

async function uploadAdImage(miniprogramId: number, file: File): Promise<string> {
  const fd = new FormData();
  fd.append('file', file);
  fd.append('miniprogram_id', String(miniprogramId));
  const res = await fetch(apiUrl('site_master_upload_ad_image'), { method: 'POST', body: fd, credentials: 'include' });
  const data = (await res.json()) as { ok?: boolean; local_path?: string; detail?: string; err?: string };
  if (!data.ok || !data.local_path) {
    throw new Error(data.detail ?? data.err ?? '上传失败');
  }
  return data.local_path;
}

export function SiteMasterAdsPage() {
  const { sites, currentSiteId } = useSiteMasterSession();
  const [miniprograms, setMiniprograms] = useState<MiniprogramOption[]>([]);
  const [mpLoading, setMpLoading] = useState(true);
  const [mpId, setMpId] = useState(0);
  const [items, setItems] = useState<MiniproAdRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [qInput, setQInput] = useState('');
  const [appliedQ, setAppliedQ] = useState('');
  const [err, setErr] = useState('');
  const [toast, setToast] = useState('');

  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState(emptyForm(0));
  const [saving, setSaving] = useState(false);
  const [uploadBusy, setUploadBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setMpLoading(true);
    void (async () => {
      try {
        const r = await apiFetch<{ ok: boolean; items: MiniprogramOption[] }>('site_master_list_miniprograms', {
          method: 'GET',
        });
        if (!cancelled && r.ok) {
          setMiniprograms(r.items ?? []);
          if ((r.items?.length ?? 0) > 0) {
            setMpId((prev) => (prev > 0 ? prev : r.items![0].id));
          }
        }
      } catch {
        if (!cancelled) setErr('无法加载小程序信息');
      } finally {
        if (!cancelled) setMpLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const load = useCallback(async () => {
    if (mpId < 1) return;
    setLoading(true);
    setErr('');
    try {
      const r = await apiFetch<{ ok: boolean; items: MiniproAdRow[]; total: number; detail?: string }>(
        'site_master_list_ads',
        {
          method: 'GET',
          query: {
            miniprogram_id: mpId,
            page,
            per_page: PER_PAGE,
            ...(appliedQ ? { q: appliedQ } : {}),
          },
        },
      );
      if (r.ok) {
        setItems(r.items ?? []);
        setTotal(r.total ?? 0);
      } else {
        setErr('加载失败');
      }
    } catch (e) {
      if (e instanceof Error && e.message !== 'SESSION_EXPIRED') {
        setErr('加载失败');
      }
    } finally {
      setLoading(false);
    }
  }, [mpId, page, appliedQ, currentSiteId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    setPage(1);
  }, [mpId, currentSiteId]);

  useEffect(() => {
    if (!toast) return;
    const id = window.setTimeout(() => setToast(''), 4500);
    return () => window.clearTimeout(id);
  }, [toast]);

  function openCreate() {
    setForm(emptyForm(mpId));
    setFormOpen(true);
  }

  function openEdit(row: MiniproAdRow) {
    setForm({ ...row, site_ids: row.site_ids ?? [] });
    setFormOpen(true);
  }

  async function saveAd() {
    setSaving(true);
    setToast('');
    try {
      const r = await apiFetch<{ ok: boolean; err?: string; detail?: string }>('site_master_save_ad', {
        method: 'POST',
        body: JSON.stringify({ ...form, miniprogram_id: mpId }),
      });
      if (!r.ok) {
        setToast(r.detail ?? r.err ?? '保存失败');
        return;
      }
      setFormOpen(false);
      setToast('广告已保存');
      await load();
    } finally {
      setSaving(false);
    }
  }

  async function removeAd(id: number) {
    if (!window.confirm('确定删除该广告？')) return;
    const r = await apiFetch<{ ok: boolean; detail?: string }>('site_master_delete_ad', {
      method: 'POST',
      body: JSON.stringify({ id, miniprogram_id: mpId }),
    });
    if (r.ok) {
      setToast('已删除');
      await load();
    } else {
      setToast(r.detail ?? '删除失败');
    }
  }

  async function pickImage(field: 'banner_image_path' | 'target_image_path', file: File) {
    setUploadBusy(true);
    try {
      const path = await uploadAdImage(mpId, file);
      setForm((prev) => ({ ...prev, [field]: path }));
    } catch (e) {
      setToast(e instanceof Error ? e.message : '上传失败');
    } finally {
      setUploadBusy(false);
    }
  }

  function toggleSiteId(siteId: number) {
    setForm((prev) => {
      const set = new Set(prev.site_ids);
      if (set.has(siteId)) set.delete(siteId);
      else set.add(siteId);
      return { ...prev, site_ids: [...set].sort((a, b) => a - b) };
    });
  }

  const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));

  if (mpLoading) {
    return <Spinner label="加载中" />;
  }

  if (miniprograms.length === 0) {
    return (
      <section className="rounded-3xl border-2 border-amber-200 bg-amber-50 p-6 text-center">
        <p className="text-lg font-semibold text-amber-950">暂无可管理的小程序</p>
        <p className="mt-2 text-base text-amber-900">
          您的站点尚未关联小程序，请联系管理员在「小程序管理」中配置数据站点。
        </p>
        <Link to={siteMasterPaths.home} className="mt-4 inline-block text-teal-700 underline">
          返回首页
        </Link>
      </section>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">站点广告</h1>
        <p className="mt-1 text-base text-slate-600">
          管理首页列表上方 5:1 横条广告，与后台「广告管理」一致。轮播顺序跟随总后台「置顶 / 广告栏排序方式」（默认按有效期剩余天数，越多越靠前）。
        </p>
      </div>

      {miniprograms.length > 1 ? (
        <div className="flex flex-wrap gap-2">
          {miniprograms.map((mp) => (
            <button
              key={mp.id}
              type="button"
              onClick={() => setMpId(mp.id)}
              className={cn(
                'rounded-full px-4 py-2 text-base font-semibold',
                mpId === mp.id ? 'bg-amber-500 text-white' : 'bg-slate-100 text-slate-700',
              )}
            >
              {mp.name || `小程序 #${mp.id}`}
            </button>
          ))}
        </div>
      ) : null}

      <button
        type="button"
        onClick={openCreate}
        className="flex w-full items-center justify-center gap-2.5 rounded-2xl bg-amber-500 py-4 text-xl font-bold text-white shadow-lg shadow-amber-500/25 ring-2 ring-amber-600/20 active:bg-amber-600"
      >
        <i className="fa-solid fa-plus text-lg" aria-hidden />
        新建广告
      </button>

      <div className="flex gap-2">
        <input
          className={`${inputClass} flex-1`}
          placeholder="搜索标题 / ID / 信息 ID"
          value={qInput}
          onChange={(e) => setQInput(e.target.value)}
        />
        <button
          type="button"
          onClick={() => {
            setAppliedQ(qInput.trim());
            setPage(1);
          }}
          className="shrink-0 rounded-2xl bg-slate-800 px-5 text-lg font-bold text-white"
        >
          搜
        </button>
      </div>

      {toast ? (
        <div
          className="fixed inset-x-4 bottom-[calc(5.75rem+env(safe-area-inset-bottom))] z-[70] rounded-2xl bg-emerald-50 px-4 py-3 text-lg text-emerald-900 shadow-lg"
          role="status"
        >
          {toast}
        </div>
      ) : null}
      {err ? <p className="rounded-2xl bg-red-50 px-4 py-3 text-lg text-red-900">{err}</p> : null}

      {loading ? (
        <Spinner label="加载广告" />
      ) : items.length === 0 ? (
        <p className="py-12 text-center text-lg text-slate-500">暂无广告，点击「新建广告」添加。</p>
      ) : (
        <ul className="space-y-3">
          {items.map((row) => (
            <li key={row.id} className="overflow-hidden rounded-2xl border-2 border-slate-200 bg-white">
              <div className="p-4">
                {row.banner_image_path ? (
                  <img
                    src={resolveMediaUrl(row.banner_image_path)}
                    alt=""
                    className="mb-3 h-14 w-full max-w-[280px] rounded-lg border object-cover"
                    style={{ aspectRatio: '5 / 1' }}
                  />
                ) : null}
                <p className="text-lg font-semibold text-slate-900">{row.title || `广告 #${row.id}`}</p>
                <p className="mt-1 text-base text-slate-600">
                  {row.link_type === 'entry' ? `跳转信息 #${row.entry_id}` : '点击图片展示'}
                  {' · '}
                  {row.valid_start_date} ~ {row.valid_end_date}
                  {typeof row.remaining_days === 'number' ? ` · 剩 ${row.remaining_days} 天` : ''}
                </p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {row.is_active ? (
                    <span className="rounded-full bg-emerald-50 px-2.5 py-0.5 text-sm font-medium text-emerald-800 ring-1 ring-emerald-200">
                      展示中
                    </span>
                  ) : (
                    <span className="rounded-full bg-amber-50 px-2.5 py-0.5 text-sm font-medium text-amber-900 ring-1 ring-amber-200">
                      未在有效期
                    </span>
                  )}
                  {!row.enabled ? (
                    <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-sm text-slate-600">已停用</span>
                  ) : null}
                </div>
              </div>
              <div className="flex gap-2 border-t border-slate-100 bg-slate-50/60 px-3 py-2.5">
                <button
                  type="button"
                  onClick={() => openEdit(row)}
                  className="flex flex-1 items-center justify-center gap-1.5 rounded-xl border border-slate-200 bg-white py-3 text-base font-semibold text-slate-800"
                >
                  编辑
                </button>
                <button
                  type="button"
                  onClick={() => void removeAd(row.id)}
                  className="flex flex-1 items-center justify-center gap-1.5 rounded-xl border border-red-200 bg-red-50 py-3 text-base font-semibold text-red-800"
                >
                  删除
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {totalPages > 1 ? (
        <div className="flex items-center justify-between gap-3 pt-2">
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className="flex-1 rounded-2xl border-2 border-slate-300 py-3 text-lg font-semibold disabled:opacity-40"
          >
            上一页
          </button>
          <span className="text-lg text-slate-600">
            {page} / {totalPages}
          </span>
          <button
            type="button"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
            className="flex-1 rounded-2xl border-2 border-slate-300 py-3 text-lg font-semibold disabled:opacity-40"
          >
            下一页
          </button>
        </div>
      ) : null}

      {formOpen ? (
        <div className="fixed inset-0 z-50 flex flex-col bg-white" style={{ paddingTop: 'env(safe-area-inset-top)' }}>
          <header className="flex items-center gap-3 border-b border-slate-200 px-4 py-3">
            <button
              type="button"
              disabled={saving}
              onClick={() => setFormOpen(false)}
              className="rounded-xl border-2 border-slate-200 px-4 py-2 text-lg font-semibold"
            >
              返回
            </button>
            <h2 className="text-xl font-bold text-slate-900">{form.id > 0 ? `编辑广告 #${form.id}` : '新建广告'}</h2>
          </header>
          <div className="flex-1 space-y-4 overflow-y-auto px-4 py-4">
            <label className="block">
              <span className="mb-2 block text-lg font-semibold text-slate-700">标题（管理用）</span>
              <input
                className={inputClass}
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
              />
            </label>

            <div>
              <span className="mb-2 block text-lg font-semibold text-slate-700">横条广告图（5:1）</span>
              {form.banner_image_path ? (
                <img
                  src={resolveMediaUrl(form.banner_image_path)}
                  alt=""
                  className="mb-2 h-16 w-full max-w-sm rounded-lg border object-cover"
                  style={{ aspectRatio: '5 / 1' }}
                />
              ) : null}
              <label className="inline-flex cursor-pointer items-center gap-2 rounded-xl border-2 border-slate-200 px-4 py-2.5 text-base font-semibold text-slate-700">
                {uploadBusy ? '上传中…' : '上传横条图'}
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  disabled={uploadBusy || saving}
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    e.target.value = '';
                    if (f) void pickImage('banner_image_path', f);
                  }}
                />
              </label>
            </div>

            <label className="block">
              <span className="mb-2 block text-lg font-semibold text-slate-700">点击行为</span>
              <select
                className={inputClass}
                value={form.link_type}
                onChange={(e) =>
                  setForm({
                    ...form,
                    link_type: e.target.value === 'image' ? 'image' : 'entry',
                  })
                }
              >
                <option value="entry">打开某条信息详情</option>
                <option value="image">打开图片（如二维码）</option>
              </select>
            </label>

            {form.link_type === 'entry' ? (
              <label className="block">
                <span className="mb-2 block text-lg font-semibold text-slate-700">信息 ID</span>
                <input
                  className={inputClass}
                  type="number"
                  min={1}
                  value={form.entry_id || ''}
                  onChange={(e) => setForm({ ...form, entry_id: Number(e.target.value) || 0 })}
                />
              </label>
            ) : (
              <div>
                <span className="mb-2 block text-lg font-semibold text-slate-700">点击展示图片</span>
                {form.target_image_path ? (
                  <img
                    src={resolveMediaUrl(form.target_image_path)}
                    alt=""
                    className="mb-2 h-24 w-24 rounded-lg border object-cover"
                  />
                ) : null}
                <label className="inline-flex cursor-pointer items-center gap-2 rounded-xl border-2 border-slate-200 px-4 py-2.5 text-base font-semibold text-slate-700">
                  {uploadBusy ? '上传中…' : '上传图片'}
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    disabled={uploadBusy || saving}
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      e.target.value = '';
                      if (f) void pickImage('target_image_path', f);
                    }}
                  />
                </label>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="mb-2 block text-base font-semibold text-slate-700">生效开始</span>
                <input
                  type="date"
                  className={inputClass}
                  value={form.valid_start_date}
                  onChange={(e) => setForm({ ...form, valid_start_date: e.target.value })}
                />
              </label>
              <label className="block">
                <span className="mb-2 block text-base font-semibold text-slate-700">生效结束</span>
                <input
                  type="date"
                  className={inputClass}
                  value={form.valid_end_date}
                  onChange={(e) => setForm({ ...form, valid_end_date: e.target.value })}
                />
              </label>
            </div>

            <label className="block">
              <span className="mb-2 block text-base font-semibold text-slate-700">
                同优先级次序（数字越小越靠前）
              </span>
              <input
                className={inputClass}
                type="number"
                value={form.sort_order}
                onChange={(e) => setForm({ ...form, sort_order: Number(e.target.value) || 0 })}
              />
              <p className="mt-1 text-sm text-slate-500">主排序由总后台「置顶 / 广告栏排序方式」决定，此项仅在剩余天数等相同时生效。</p>
            </label>

            <label className="flex cursor-pointer items-center gap-3 text-lg text-slate-800">
              <input
                type="checkbox"
                className="h-5 w-5 rounded border-slate-300"
                checked={form.enabled}
                onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
              />
              启用
            </label>

            {sites.length > 0 ? (
              <div>
                <p className="mb-2 text-base font-semibold text-slate-700">展示站点（不选表示全站）</p>
                <ul className="space-y-2">
                  {sites.map((s) => {
                    const checked = form.site_ids.includes(s.id);
                    return (
                      <li key={s.id}>
                        <label
                          className={cn(
                            'flex cursor-pointer items-center gap-3 rounded-2xl border-2 px-4 py-3 text-lg font-semibold',
                            checked ? 'border-amber-400 bg-amber-50 text-amber-950' : 'border-slate-200 bg-white',
                          )}
                        >
                          <input
                            type="checkbox"
                            className="h-5 w-5 rounded border-slate-300"
                            checked={checked}
                            onChange={() => toggleSiteId(s.id)}
                          />
                          {s.site_name}
                        </label>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ) : null}

            <button
              type="button"
              disabled={saving || uploadBusy}
              onClick={() => void saveAd()}
              className="w-full rounded-2xl bg-amber-500 py-4 text-xl font-bold text-white disabled:opacity-60"
            >
              {saving ? '保存中…' : '保存广告'}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
